import { useEffect, useRef, useState } from "react";
import { api, formatApiError, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Boxes, Trash2, Loader2, Package } from "lucide-react";

const EMPTY_IT = { kind: "servico", description: "", quantity: 1, unit_price: 0, discount: 0 };

function KitDialog({ open, onOpenChange, kit, onSaved }) {
  const [f, setF] = useState({ name: "", note: "", items: [] });
  const [busy, setBusy] = useState(false);
  const [products, setProducts] = useState([]);
  useEffect(() => { if (open) { api.get("/products").then(({ data }) => setProducts(data.items || [])).catch(() => setProducts([])); } }, [open]);
  const pickProduct = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    setF((prev) => ({ ...prev, items: prev.items.map((it, idx) => (idx === i ? (p ? { ...it, product_id: p.id, description: p.description, unit_price: p.price || 0 } : { ...it, product_id: null }) : it)) }));
  };
  useEffect(() => { if (open) { setF(kit ? { ...kit, items: kit.items || [] } : { name: "", note: "", items: [{ ...EMPTY_IT }] }); } }, [open, kit]);
  const addRow = () => setF((p) => ({ ...p, items: [...p.items, { ...EMPTY_IT }] }));
  const rmRow = (i) => setF((p) => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));
  const setRow = (i, k, v) => setF((p) => ({ ...p, items: p.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Informe o nome do kit"); return; }
    setBusy(true);
    try {
      const body = { name: f.name.trim(), note: f.note || null, items: f.items.filter((i) => i.description).map((i) => ({ ...i, quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0, discount: Number(i.discount) || 0 })) };
      if (kit) { await api.put(`/service-kits/${kit.id}`, body); } else { await api.post("/service-kits", body); }
      toast.success("Kit salvo"); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="kit-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-2xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display uppercase">{kit ? "Editar kit" : "Novo kit"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block text-xs font-mono-caps text-zinc-400">Nome<Input data-testid="kit-name" value={f.name} onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))} className="mt-1 bg-zinc-900 border-zinc-800" placeholder="Troca de óleo completa" /></label>
          <div className="space-y-2">
            {f.items.map((it, i) => (
              <div key={i} data-testid={`kit-row-${i}`} className="grid grid-cols-12 gap-2 items-end">
                <select data-testid={`kit-row-${i}-kind`} value={it.kind} onChange={(e) => setRow(i, "kind", e.target.value)} className="col-span-3 h-9 rounded-md bg-zinc-900 border border-zinc-800 text-sm px-2">
                  <option value="servico">Serviço</option><option value="produto">Peça</option>
                </select>
                {it.kind === "produto" ? (
                  <select data-testid={`kit-row-${i}-product`} value={it.product_id || ""} onChange={(e) => pickProduct(i, e.target.value)} className="col-span-5 h-9 rounded-md bg-zinc-900 border border-zinc-800 text-sm px-2 min-w-0">
                    <option value="">{it.description && !it.product_id ? it.description : "Escolha a peça do estoque…"}</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ` : ""}{p.description}</option>)}
                  </select>
                ) : (
                  <Input data-testid={`kit-row-${i}-desc`} className="col-span-5 bg-zinc-900 border-zinc-800" placeholder="Descrição do serviço" value={it.description} onChange={(e) => setRow(i, "description", e.target.value)} />
                )}
                <Input data-testid={`kit-row-${i}-qty`} type="number" step="0.1" className="col-span-1 bg-zinc-900 border-zinc-800" value={it.quantity} onChange={(e) => setRow(i, "quantity", e.target.value)} />
                <Input data-testid={`kit-row-${i}-price`} type="number" step="0.01" className="col-span-2 bg-zinc-900 border-zinc-800" value={it.unit_price} onChange={(e) => setRow(i, "unit_price", e.target.value)} placeholder="R$" />
                <button type="button" data-testid={`kit-row-${i}-rm`} onClick={() => rmRow(i)} className="col-span-1 h-9 text-zinc-500 hover:text-red-400 flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" data-testid="kit-add-row" onClick={addRow} className="bg-zinc-900 border-zinc-800"><Plus className="h-4 w-4 mr-1" /> Adicionar item</Button>
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-900">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="bg-zinc-900 border-zinc-800">Cancelar</Button>
            <Button type="button" data-testid="kit-save" onClick={save} disabled={busy} className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ServiceKitsCard() {
  const [list, setList] = useState(null);
  const [edit, setEdit] = useState({ open: false, kit: null });
  const load = () => api.get("/service-kits").then(({ data }) => setList(data)).catch((e) => { toast.error(formatApiError(e)); setList([]); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (k) => { if (!window.confirm(`Excluir o kit "${k.name}"?`)) { return; } try { await api.delete(`/service-kits/${k.id}`); toast.success("Kit excluído"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div data-testid="service-kits-card" className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 md:p-7">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2"><Package className="h-4 w-4 text-green-brand" /><h2 className="font-display text-lg font-bold uppercase text-zinc-100">Kits de serviço</h2></div>
        <Button type="button" size="sm" data-testid="kit-new" onClick={() => setEdit({ open: true, kit: null })} className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase"><Plus className="h-4 w-4 mr-1" /> Novo kit</Button>
      </div>
      <p className="text-xs text-zinc-500 mb-3">Monte pacotes como "Troca de óleo completa". No orçamento um clique adiciona todos os itens; depois dá para editar normalmente.</p>
      {list === null ? <Loader2 className="h-5 w-5 animate-spin text-zinc-500" /> : list.length === 0 ? (
        <div className="text-sm text-zinc-500">Nenhum kit ainda.</div>
      ) : (
        <ul className="divide-y divide-zinc-800">
          {list.map((k) => (
            <li key={k.id} data-testid={`kit-item-${k.id}`} className="flex items-center gap-3 py-2">
              <div className="flex-1 min-w-0"><div className="text-sm font-bold text-zinc-100 truncate">{k.name}</div><div className="text-[11px] text-zinc-500">{(k.items || []).length} item(ns) · total {brl((k.items || []).reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0) - (Number(i.discount) || 0), 0))}</div></div>
              <Button size="sm" variant="outline" data-testid={`kit-edit-${k.id}`} onClick={() => setEdit({ open: true, kit: k })} className="bg-zinc-900 border-zinc-800">Editar</Button>
              <button type="button" data-testid={`kit-del-${k.id}`} onClick={() => del(k)} className="text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
      <KitDialog open={edit.open} kit={edit.kit} onOpenChange={(o) => setEdit((p) => ({ ...p, open: o }))} onSaved={load} />
    </div>
  );
}

export function KitList({ onPick }) {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/service-kits").then(({ data }) => setList(data)).catch(() => setList([])); }, []);
  return (
    <div data-testid="apply-kit-menu" className="mt-2 rounded-xl border border-zinc-800 bg-zinc-950 max-h-64 overflow-y-auto">
      {list === null ? <div className="p-3 text-xs text-zinc-500">Carregando…</div> : list.length === 0 ? (
        <div className="p-3 text-xs text-zinc-500">Nenhum kit ainda. Cadastre em Configurações › Oficina.</div>
      ) : list.map((k) => (
        <button key={k.id} type="button" data-testid={`apply-kit-${k.id}`} onClick={() => { onPick(k.items || []); toast.success(`Kit "${k.name}" aplicado`); }} className="w-full text-left px-3 py-2.5 text-sm text-zinc-200 hover:bg-zinc-900 border-b border-zinc-900 last:border-0">
          <div className="font-bold">{k.name}</div><div className="text-[11px] text-zinc-500">{(k.items || []).length} item(ns)</div>
        </button>
      ))}
    </div>
  );
}

export function ApplyKitButton({ onApply }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const btnRef = useRef(null);
  useEffect(() => { if (open && list === null) { api.get("/service-kits").then(({ data }) => setList(data)).catch(() => setList([])); } }, [open, list]);
  const pick = (k) => { onApply(k.items || []); setOpen(false); toast.success(`Kit "${k.name}" aplicado`); };
  return (
    <div className="relative">
      <Button ref={btnRef} type="button" variant="outline" data-testid="budget-apply-kit" onClick={() => setOpen((o) => !o)} className="bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800">
        <Boxes className="h-4 w-4 mr-1" /> Aplicar kit
      </Button>
      {open && (
        <div data-testid="apply-kit-menu" className="absolute left-0 top-full mt-1 z-20 min-w-[14rem] max-h-72 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950 shadow-xl">
          {list === null ? <div className="p-3 text-xs text-zinc-500">Carregando…</div> : list.length === 0 ? (
            <div className="p-3 text-xs text-zinc-500">Cadastre kits em Configurações.</div>
          ) : list.map((k) => (
            <button key={k.id} type="button" data-testid={`apply-kit-${k.id}`} onClick={() => pick(k)} className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-900">
              <div className="font-bold">{k.name}</div><div className="text-[11px] text-zinc-500">{(k.items || []).length} item(ns)</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
